// frontend/src/pages/BarLandingPage.tsx
//
// /bar/<ref> - the bar-wedge smart link (one permanent QR per bar).
// Stores the bar's ref for signup attribution, counts the scan, then routes
// to the active/next-upcoming weekend contest's Builder (same priority logic
// as the Lobby's featured contest). Falls back to the Lobby - never a dead end.

import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchActiveContests, fetchUpcomingContests } from '@/api/dfs';
import { recordReferralScan } from '@/api/referrals';
import Spinner from '@/components/common/Spinner';
import { getFeaturedContest } from '@/utils/featuredContest';
import { normalizeRef, saveReferral } from '@/utils/referral';
import { mixpanelTrack } from '@/services/mixpanel';
import { venueTheme } from '@/utils/venues';

const BarLandingPage: React.FC = () => {
  const { ref: refParam } = useParams<{ ref: string }>();
  const navigate = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // resolve once (StrictMode double-mount guard)
    ranRef.current = true;

    // A partner bar with its own contest: its page records the scan and
    // routes by the contest's phase (BAR-CONTESTS-SCOPE-2026-10-06.md).
    const venue = venueTheme(refParam);
    if (venue) {
      navigate(`/${venue.slug}`, { replace: true });
      return;
    }

    const ref = normalizeRef(refParam);
    if (ref) {
      saveReferral(ref);
      recordReferralScan(ref);
      mixpanelTrack('Bar QR Scan', { 'Ref': ref });
    }

    const resolve = async () => {
      try {
        const [active, upcoming] = await Promise.all([
          fetchActiveContests(null),
          fetchUpcomingContests(null, 14),
        ]);
        const featured = getFeaturedContest(active, upcoming);
        if (featured) {
          navigate(`/dfs/build/${featured.contest.id}`, { replace: true });
          return;
        }
      } catch {
        // fall through to the Lobby
      }
      navigate('/contests', { replace: true });
    };
    void resolve();
  }, [refParam, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-dark">
      <Spinner />
    </div>
  );
};

export default BarLandingPage;
